import api from './api';
import { toast } from 'sonner';

/**
 * V14.5 Part 9 — B7 fix: mobile-safe Zoom join.
 *
 * Mobile browsers (especially iOS Safari) block `window.open` calls that
 * are not directly inside a user-gesture (i.e. anything called after an
 * `await`). Before this helper, the Join button:
 *
 *   onClick={async () => {
 *     const r = await api.get('/api/sessions/X/link');  // <-- gesture lost here
 *     window.open(r.data.zoom_join_url, '_blank');     // BLOCKED on iOS
 *   }}
 *
 * Now we open a placeholder window SYNCHRONOUSLY inside the click handler
 * (which preserves the gesture) and redirect it to the real URL once the
 * API call resolves. Falls back to same-tab navigation when the popup is
 * blocked anyway (very-locked-down browsers).
 *
 * @param {string} sessionId  Session ID to fetch the latest join URL for.
 * @param {string|null} fallbackUrl  Optional cached URL used if the API
 *                                   call fails (e.g. offline).
 * @returns {Promise<boolean>} true if a join was launched.
 */
export async function joinClass(sessionId, fallbackUrl = null) {
  // Open the placeholder SYNCHRONOUSLY. Browser allows this because we're
  // still inside the user-gesture stack of the click event.
  const placeholder = window.open('', '_blank');
  const navigateTo = (url) => {
    if (placeholder && !placeholder.closed) {
      try { placeholder.location.href = url; return; } catch { /* fallthrough */ }
    }
    window.location.href = url;
  };
  const closePlaceholder = () => {
    if (placeholder && !placeholder.closed) {
      try { placeholder.close(); } catch { /* noop */ }
    }
  };

  if (!sessionId) {
    closePlaceholder();
    toast.info('Zoom meeting link will be available when the class is about to start.');
    return false;
  }

  try {
    const res = await api.get(`/api/sessions/${sessionId}/link`);
    const url = res.data?.zoom_join_url || res.data?.zoom_join_web_url || fallbackUrl;
    if (!url) {
      closePlaceholder();
      toast.info('Zoom meeting link will be available when the class is about to start.');
      return false;
    }
    navigateTo(url);
    return true;
  } catch (err) {
    if (fallbackUrl) {
      navigateTo(fallbackUrl);
      return true;
    }
    closePlaceholder();
    toast.error(err?.response?.data?.detail || 'Failed to fetch Zoom link');
    return false;
  }
}
